import { FC, FormEvent, useEffect, useState } from 'react';
import { Alert, Spinner } from 'react-bootstrap';
import type { CompanyGroupRecord, CompanyLocationRecord } from '../admin/clients/api';
import {
  GroupModal,
  LocationModal,
  LocationsTab,
  TeamsGroupsTab,
  emptyGroupFormState,
  emptyLocationFormState,
  groupToFormState,
  locationToFormState,
  type CompanyGroupFormState,
  type CompanyLocationFormState,
  type GroupFieldErrors,
  type LocationFieldErrors,
} from '../admin/clients/clients-page';
import {
  deleteSharedCompanyGroup,
  deleteSharedCompanyLocation,
  fetchSharedCompanyGroups,
  fetchSharedCompanyLocations,
  saveSharedCompanyGroup,
  saveSharedCompanyLocation,
} from './api';
import { CompanyResourceBulkSetup } from './company-resource-spreadsheet-import';
import EvidenceReviewWorkspace from '../schedule-settings/evidence-review-workspace';
import '../schedule-settings/schedule-settings-page.scss';

type SharedCompanyResourceWorkspaceProps = {
  resource: 'locations' | 'groups';
  tenantQuery: string;
};

const sortLocations = (records: CompanyLocationRecord[]) =>
  [...records].sort((left, right) => left.display_name.localeCompare(right.display_name));

const sortGroups = (records: CompanyGroupRecord[]) =>
  [...records].sort((left, right) => left.kind.localeCompare(right.kind) || left.name.localeCompare(right.name));

const SharedCompanyResourceWorkspace: FC<SharedCompanyResourceWorkspaceProps> = ({ resource, tenantQuery }) => {
  const [locations, setLocations] = useState<CompanyLocationRecord[]>([]);
  const [groups, setGroups] = useState<CompanyGroupRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [busyRecordId, setBusyRecordId] = useState<number | null>(null);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [locationDraft, setLocationDraft] = useState<CompanyLocationFormState>(emptyLocationFormState);
  const [groupDraft, setGroupDraft] = useState<CompanyGroupFormState>(emptyGroupFormState);
  const [locationFieldErrors, setLocationFieldErrors] = useState<LocationFieldErrors>({});
  const [groupFieldErrors, setGroupFieldErrors] = useState<GroupFieldErrors>({});

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const request = resource === 'locations'
      ? fetchSharedCompanyLocations(tenantQuery).then((records) => {
          if (!cancelled) setLocations(sortLocations(records));
        })
      : fetchSharedCompanyGroups(tenantQuery).then((records) => {
          if (!cancelled) setGroups(sortGroups(records));
        });

    void request
      .catch((requestError) => {
        if (!cancelled) setError(requestError instanceof Error ? requestError.message : `Unable to load ${resource}.`);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [resource, tenantQuery]);

  const saveLocation = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);
    setLocationFieldErrors({});

    try {
      const record = await saveSharedCompanyLocation(tenantQuery, locationDraft.id, {
        code: locationDraft.code.trim(),
        display_name: locationDraft.display_name.trim(),
        public_label: locationDraft.public_label.trim(),
        location_group: locationDraft.location_group.trim(),
        timezone: locationDraft.timezone.trim(),
        is_active: locationDraft.is_active,
        notes: locationDraft.notes.trim(),
      });
      setLocations((current) => sortLocations([...current.filter((item) => item.id !== record.id), record]));
      setShowLocationModal(false);
      setSuccess(locationDraft.id === null ? 'Location created.' : 'Location updated.');
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to save location.');
    } finally {
      setSaving(false);
    }
  };

  const saveGroup = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);
    setGroupFieldErrors({});

    try {
      const record = await saveSharedCompanyGroup(tenantQuery, groupDraft.id, {
        parent_group_id: groupDraft.parent_group_id,
        name: groupDraft.name.trim(),
        slug: groupDraft.slug.trim(),
        kind: groupDraft.kind.trim(),
        status: groupDraft.status,
      });
      setGroups((current) => sortGroups([...current.filter((item) => item.id !== record.id), record]));
      setShowGroupModal(false);
      const label = record.is_department ? 'Department' : 'Schedule Group';
      setSuccess(groupDraft.id === null ? `${label} created.` : `${label} updated.`);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to save Department or Schedule Group.');
    } finally {
      setSaving(false);
    }
  };

  const deleteLocation = async (record: CompanyLocationRecord) => {
    if (!window.confirm(`Delete location "${record.display_name}"?`)) return;
    setBusyRecordId(record.id);
    setError(null);
    try {
      await deleteSharedCompanyLocation(tenantQuery, record.id);
      setLocations((current) => current.filter((item) => item.id !== record.id));
      setSuccess('Location deleted.');
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to delete location.');
    } finally {
      setBusyRecordId(null);
    }
  };

  const deleteGroup = async (record: CompanyGroupRecord) => {
    const label = record.is_department ? 'Department' : 'Schedule Group';
    if (!window.confirm(`Delete ${label.toLowerCase()} "${record.name}"?`)) return;
    setBusyRecordId(record.id);
    setError(null);
    try {
      await deleteSharedCompanyGroup(tenantQuery, record.id);
      setGroups((current) => current.filter((item) => item.id !== record.id));
      setSuccess(`${label} deleted.`);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : `Unable to delete ${label.toLowerCase()}.`);
    } finally {
      setBusyRecordId(null);
    }
  };

  const refreshLocations = async () => {
    try {
      setLocations(sortLocations(await fetchSharedCompanyLocations(tenantQuery)));
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to refresh locations.');
    }
  };

  return (
    <>
      {loading ? <Alert variant="info"><Spinner animation="border" size="sm" className="me-2" />Loading {resource}…</Alert> : null}
      {error ? <Alert variant="danger">{error}</Alert> : null}
      {success ? <Alert variant="success">{success}</Alert> : null}

      {resource === 'locations' ? (
        <>
          <LocationsTab
            locations={locations}
            busyRecordId={busyRecordId}
            onCreate={() => {
              setLocationDraft(emptyLocationFormState);
              setLocationFieldErrors({});
              setShowLocationModal(true);
            }}
            onEdit={(record) => {
              setLocationDraft(locationToFormState(record));
              setLocationFieldErrors({});
              setShowLocationModal(true);
            }}
            onDelete={(record) => void deleteLocation(record)}
          />
          <div className="schedule-settings-page">
            <EvidenceReviewWorkspace
              kind="location"
              tenantQuery={tenantQuery}
              administrativeLocations
              onRecordsChanged={refreshLocations}
            />
          </div>
        </>
      ) : (
        <TeamsGroupsTab
          groups={groups}
          busyRecordId={busyRecordId}
          onCreate={(kind) => {
            setGroupDraft({ ...emptyGroupFormState, kind });
            setGroupFieldErrors({});
            setShowGroupModal(true);
          }}
          onEdit={(record) => {
            setGroupDraft(groupToFormState(record));
            setGroupFieldErrors({});
            setShowGroupModal(true);
          }}
          onDelete={(record) => void deleteGroup(record)}
        />
      )}

      <CompanyResourceBulkSetup
        resource={resource}
        tenantQuery={tenantQuery}
        locations={locations}
        groups={groups}
        onLocationsImported={(records) => {
          setLocations((current) => sortLocations([...current, ...records]));
          setSuccess(`${records.length} locations imported.`);
        }}
        onGroupsImported={(records) => {
          setGroups((current) => sortGroups([...current, ...records]));
          setSuccess(`${records.length} Departments / Groups imported.`);
        }}
      />

      <LocationModal
        draft={locationDraft}
        fieldErrors={locationFieldErrors}
        saving={saving}
        show={showLocationModal}
        onChange={(field, value) => setLocationDraft((current) => ({ ...current, [field]: value }))}
        onHide={() => setShowLocationModal(false)}
        onSubmit={saveLocation}
      />
      <GroupModal
        draft={groupDraft}
        groups={groups}
        fieldErrors={groupFieldErrors}
        saving={saving}
        show={showGroupModal}
        onChange={(field, value) => setGroupDraft((current) => ({ ...current, [field]: value }))}
        onHide={() => setShowGroupModal(false)}
        onSubmit={saveGroup}
      />
    </>
  );
};

export default SharedCompanyResourceWorkspace;
